/**
 * Mobile expand component that handles swipe gestures to open/close expandable content
 *
 * Behavior:
 * - When closed: Swipe up to open
 * - When open: Allows native scrolling of content
 * - When open: Swipe down from top (or if not scrollable) to close
 * - Locks body scroll when expanded using util-SiteOverflowed class
 */
class MobileExpand extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    // Track touch position and scroll state at touch start
    this.touchStartY = 0
    this.touchStartScrollTop = 0
  }

  setupListeners () {
    super.setupListeners()

    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    this.onToggle = this.onToggle.bind(this)

    // Touch events for swipe gestures (passive for better scroll performance)
    this.listeners.add(this, 'touchstart', this.onTouchStart, {
      passive: true,
    })
    this.listeners.add(this, 'touchend', this.onTouchEnd)

    // Click events for toggle button and backdrop
    this.listeners.add(this.els.toggle.element, 'click', this.onToggle)

    this.listeners.add(
      this.externalEls.backdrop.element,
      'click',
      this.onToggle,
    )

    // Auto-close when adding to cart
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      () => {
        this.close()
      },
    )

    // Optional external toggle button support
    if (!this.externalEls?.toggle?.exists) return

    this.listeners.add(this.externalEls.toggle.element, 'click', this.onToggle)
  }

  /**
   * Record initial touch position and scroll state
   */
  onTouchStart (event) {
    this.touchStartY = event.changedTouches[0].screenY
    this.touchStartScrollTop = this.scrollTop
  }

  /**
   * Handle touch end to determine if gesture should open/close menu
   *
   * Logic:
   * - Calculates touch delta (swipe direction/distance)
   * - Calculates scroll delta (if user actually scrolled content)
   * - When expanded: Close only if swiping down AND (not scrollable OR at top) AND didn't scroll
   * - When collapsed: Open if swiping up
   */
  onTouchEnd (event) {
    const touchEndY = event.changedTouches[0].screenY
    const touchDeltaY = touchEndY - this.touchStartY // Positive = swipe down, Negative = swipe up
    const scrollDelta = this.scrollTop - this.touchStartScrollTop // Did the content actually scroll?

    const isExpanded =
      this.els.content.element.getAttribute('aria-hidden') === 'false'

    // Check if content is scrollable (with 1px tolerance for sub-pixel rendering)
    const scrollableHeight = this.scrollHeight - this.clientHeight
    const isScrollable = scrollableHeight > 1

    let shouldClose = false

    if (isExpanded) {
      // Only close if:
      // 1. Swiping down (touchDeltaY > 0)
      // 2. Content is not scrollable OR scrolled to top (scrollTop === 0)
      // 3. User didn't actually scroll the content (scrollDelta === 0)
      if (
        touchDeltaY > 0 &&
        (!isScrollable || this.scrollTop === 0) &&
        scrollDelta === 0
      ) {
        shouldClose = true
      }
    } else {
      // Menu is closed - swipe up to open
      if (touchDeltaY < 0) {
        this.open()
      }
    }

    if (shouldClose) {
      this.close()
    }
  }

  /**
   * Open the expandable content and lock body scroll
   */
  open () {
    this.els.content.element.setAttribute('aria-hidden', false)
    this.externalEls.backdrop.element.setAttribute('aria-hidden', false)
    document.documentElement.classList.add('util-SiteOverflowed')
  }

  /**
   * Close the expandable content and unlock body scroll
   */
  close () {
    this.els.content.element.setAttribute('aria-hidden', true)
    this.externalEls.backdrop.element.setAttribute('aria-hidden', true)
    document.documentElement.classList.remove('util-SiteOverflowed')
  }

  /**
   * Toggle between open and closed states (triggered by clicks)
   */
  onToggle () {
    const currentState = this.els.content.element.getAttribute('aria-hidden')

    if (currentState === 'false') {
      this.close()
    } else {
      this.open()
    }
  }
}

if (!customElements.get('mobile-expand')) {
  customElements.define('mobile-expand', MobileExpand)
}
